import PropTypes from 'prop-types'

import ColumnGridPanel from 'components/ColumnGridPanel'

import { amps, degreesFahrenheit, percentage, pressure, rpsToRpm, rpm, rps, rpmToPwm, voltageAC, voltageDC, watts } from 'helpers/formatters'

const propTypes = {
  report: PropTypes.shape()
}

const defaultProps = {
  report: {}
}

const COMPRESSOR_SPEED_TOOLTIP = 'This value is the current compressor speed as a percentage of the maximum speed.  Compressor speed is limited in heating mode by ambient temperatures generally above 17F.'

const OutdoorUnit = ({ report }) => {
  const compressorSpeedValue = () => {
    if (!report.outdoorUnitType) {
      return null
    }

    let fieldValue = null

    if (report.compressorDeliveredCapacity != null) {
      fieldValue = percentage(report.compressorDeliveredCapacity)
    }

    if (report.extendedOutdoorCompressorActualSpeed) {
      const compressorSpeed = rpsToRpm(report.extendedOutdoorCompressorActualSpeed)

      fieldValue = fieldValue ? fieldValue + `(${compressorSpeed})` : compressorSpeed
    }

    return fieldValue
  }

  if (report?.outdoorUnitType && report?.outdoorUnitType.toLowerCase() === 'none') {
    return null
  }

  const outdoorReport = {
    compressorSpeed: compressorSpeedValue(),
    extendedOutdoorLiquidPressure: pressure(report.extendedOutdoorLiquidPressure),
    extendedOutdoorLiquidTemp: degreesFahrenheit(report.extendedOutdoorLiquidTemp, 1),
    extendedOutdoorSuctionPressure: pressure(report.extendedOutdoorSuctionPressure),
    extendedOutdoorSuctionTemp: degreesFahrenheit(report.extendedOutdoorSuctionTemp, 1),
    extendedOutdoorSubcool: degreesFahrenheit(report.extendedOutdoorSubcool, 1),
    extendedOutdoorSuperheat: degreesFahrenheit(report.extendedOutdoorSuperheat, 1),
    extendedOutdoorCoilTemp: degreesFahrenheit(report.extendedOutdoorCoilTemp, 1),
    onlineOutdoorTemperature: degreesFahrenheit(report.onlineOutdoorTemperature, 1)
  }

  const outdoorDriveTypeReport = {
    extendedOoutdoorMaxAllowedRps: rps(report.extendedOoutdoorMaxAllowedRps),
    extendedOoutdoorMinAllowedRps: rps(report.extendedOoutdoorMinAllowedRps),
    extendedOutdoorCompressorActualSpeed: rps(report.extendedOutdoorCompressorActualSpeed),
    extendedOutdoorEevStepPosition: report.extendedOutdoorEevStepPosition,
    extendedOutdoorFanSpeedPwm: report.extendedOutdoorDriveType === 'DANFOSS' ? rpmToPwm(report.extendedOutdoorFanSpeed) : null,
    extendedOutdoorFanSpeed: (report.extendedOutdoorDriveType === 'SINRAPTOR' || report.extendedOutdoorDriveType === 'RUBICON') ? rpm(report.extendedOutdoorFanSpeed) : null,
    extendedOutdoorFanPower: watts(report.extendedOutdoorFanPower),
    extendedOutdoorCompressorPower: watts(report.extendedOutdoorCompressorPower),
    extendedOutdoorLineVoltage: voltageAC(report.extendedOutdoorLineVoltage),
    extendedOutdoorInputCurrent: amps(report.extendedOutdoorInputCurrent),
    extendedOutdoorDcBusVoltage: voltageDC(report.extendedOutdoorDcBusVoltage),
    outdoorTemperature: degreesFahrenheit(report.outdoorTemperature, 1),
    extendedOutdoorCompressorDischargeTemp: degreesFahrenheit(report.extendedOutdoorCompressorDischargeTemp, 1)
  }

  const hasData = Object.values(outdoorReport).some(val => val !== null)
  const hasOutdoorData = (report.extendedOutdoorDriveType && report.extendedOutdoorDriveType !== 'UNKNOWN') && Object.values(outdoorDriveTypeReport).some(val => val !== null)

  if (!hasData && !hasOutdoorData) {
    return null
  }

  return (
    <ColumnGridPanel.Column headerContent="Outdoor Unit">
      <ColumnGridPanel.Column.Header>Outdoor Unit</ColumnGridPanel.Column.Header>
      <ColumnGridPanel.Column.Item label="Compressor Speed" value={outdoorReport.compressorSpeed} tooltip={COMPRESSOR_SPEED_TOOLTIP} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Liquid Pressure" value={outdoorReport.extendedOutdoorLiquidPressure} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Liquid Temp" value={outdoorReport.extendedOutdoorLiquidTemp} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Suction Pressure" value={outdoorReport.extendedOutdoorSuctionPressure} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Suction Temp" value={outdoorReport.extendedOutdoorSuctionTemp} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="OD Subcool" value={outdoorReport.extendedOutdoorSubcool} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="OD Superheat" value={outdoorReport.extendedOutdoorSuperheat} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="OD Coil Temp" value={outdoorReport.extendedOutdoorCoilTemp} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Display OD Temp" value={outdoorReport.onlineOutdoorTemperature} hideIfValueIsNull />
      {report.extendedOutdoorDriveType && report.extendedOutdoorDriveType !== 'UNKNOWN' && (
        <>
          <ColumnGridPanel.Column.Item label="OD Compressor Max Allowed" value={outdoorDriveTypeReport.extendedOoutdoorMaxAllowedRps} hideIfValueIsNull />
          <ColumnGridPanel.Column.Item label="OD Compressor Min Allowed" value={outdoorDriveTypeReport.extendedOoutdoorMinAllowedRps} hideIfValueIsNull />
          <ColumnGridPanel.Column.Item label="Actual Compressor Speed" value={outdoorDriveTypeReport.extendedOutdoorCompressorActualSpeed} hideIfValueIsNull />
          <ColumnGridPanel.Column.Item label="OD EEV Position" value={outdoorDriveTypeReport.extendedOutdoorEevStepPosition} hideIfValueIsNull />
          {report.extendedOutdoorDriveType === 'DANFOSS' && <ColumnGridPanel.Column.Item label="OD Fan PWM" value={outdoorDriveTypeReport.extendedOutdoorFanSpeedPwm} hideIfValueIsNull />}
          {(report.extendedOutdoorDriveType === 'SINRAPTOR' || report.extendedOutdoorDriveType === 'RUBICON') && (
            <ColumnGridPanel.Column.Item label="OD Fan RPM" value={outdoorDriveTypeReport.extendedOutdoorFanSpeed} hideIfValueIsNull />)}
          <ColumnGridPanel.Column.Item label="OD Fan Power" value={outdoorDriveTypeReport.extendedOutdoorFanPower} hideIfValueIsNull />
          <ColumnGridPanel.Column.Item label="OD Compressor Power" value={outdoorDriveTypeReport.extendedOutdoorCompressorPower} hideIfValueIsNull />
          <ColumnGridPanel.Column.Item label="OD Line Voltage" value={outdoorDriveTypeReport.extendedOutdoorLineVoltage} hideIfValueIsNull />
          <ColumnGridPanel.Column.Item label="OD Current" value={outdoorDriveTypeReport.extendedOutdoorInputCurrent} hideIfValueIsNull />
          <ColumnGridPanel.Column.Item label="OD DC Bus Voltage" value={outdoorDriveTypeReport.extendedOutdoorDcBusVoltage} hideIfValueIsNull />
          <ColumnGridPanel.Column.Item label="OD Ambient Temp" value={outdoorDriveTypeReport.outdoorTemperature} hideIfValueIsNull />
          <ColumnGridPanel.Column.Item label="Compressor Discharge Temp" value={outdoorDriveTypeReport.extendedOutdoorCompressorDischargeTemp} hideIfValueIsNull />
        </>
      )}
    </ColumnGridPanel.Column>
  )
}

OutdoorUnit.propTypes = propTypes
OutdoorUnit.defaultProps = defaultProps

export default OutdoorUnit
